/**
 * VDI / Linux Terminal Server section for a single tenant.
 * Mounted in TenantDetailPage (pattern: LicenseSection / BrandingSection).
 *
 * Lets the operator enable/disable the per-tenant Apache Guacamole + Linux
 * desktop stack and tune the warm pool (minPool = "Mindestanzahl" for low
 * logon latency), capacity (maxDesktops) and idle timeout. Provisioning is
 * AWX-mediated; the buttons launch jobs and return 202 + a jobId. Per-tenant
 * VDI usage + cost is shown via the existing usage/quota panel (vdi.* metrics).
 */
import { useEffect, useState } from 'react';
import { useBrand } from '../contexts/BrandContext';
import {
  VdiConfig, getTenantVdi, enableTenantVdi, updateTenantVdi, disableTenantVdi, extractApiError,
} from '../services/api';
import { VdiProfilesSection } from './VdiProfilesSection';

interface Props {
  slug: string;
  isReadOnly: boolean;
}

export function VdiSection({ slug, isReadOnly }: Props) {
  const { domain } = useBrand();
  const [loaded, setLoaded] = useState(false);
  const [cfg, setCfg] = useState<VdiConfig | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const [minPool, setMinPool] = useState('0');
  const [maxDesktops, setMaxDesktops] = useState('10');
  const [idleTimeoutMin, setIdleTimeoutMin] = useState('30');
  const [guacamole, setGuacamole] = useState(true);

  function apply(c: VdiConfig): void {
    setCfg(c);
    setMinPool(String(c.minPool));
    setMaxDesktops(String(c.maxDesktops));
    setIdleTimeoutMin(String(c.idleTimeoutMin));
    setGuacamole(c.guacamole ?? true);
  }

  useEffect(() => {
    let cancelled = false;
    getTenantVdi(slug).then((c) => {
      if (cancelled) return;
      apply(c); setLoaded(true);
    }).catch(() => {
      if (!cancelled) { setErr('VDI-Konfiguration konnte nicht geladen werden'); setLoaded(true); }
    });
    return () => { cancelled = true; };
  }, [slug]);

  const minPoolNum = Number(minPool);
  const maxNum = Number(maxDesktops);
  const idleNum = Number(idleTimeoutMin);
  const valid =
    Number.isInteger(minPoolNum) && minPoolNum >= 0 && minPoolNum <= 100 &&
    Number.isInteger(maxNum) && maxNum >= 1 && maxNum <= 500 &&
    Number.isInteger(idleNum) && idleNum >= 5 && idleNum <= 1440 &&
    minPoolNum <= maxNum;

  async function run(fn: () => Promise<{ jobId: number; config?: VdiConfig }>, label: string): Promise<void> {
    if (isReadOnly) return;
    setBusy(true); setErr(null); setMsg(null);
    try {
      const r = await fn();
      if (r.config) apply(r.config); else apply({ ...(cfg as VdiConfig), enabled: false });
      setMsg(`${label} gestartet (AWX-Job #${r.jobId}).`);
    } catch (e) {
      setErr(extractApiError(e));
    } finally {
      setBusy(false);
    }
  }

  const onEnable = () => {
    if (!valid) { setErr('minPool (0–100) ≤ maxDesktops (1–500), Idle 5–1440 min.'); return; }
    void run(() => enableTenantVdi(slug, { minPool: minPoolNum, maxDesktops: maxNum, idleTimeoutMin: idleNum, guacamole }), 'Aktivierung');
  };
  const onUpdate = () => {
    if (!valid) { setErr('minPool (0–100) ≤ maxDesktops (1–500), Idle 5–1440 min.'); return; }
    void run(() => updateTenantVdi(slug, { minPool: minPoolNum, maxDesktops: maxNum, idleTimeoutMin: idleNum, guacamole }), 'Aktualisierung');
  };
  const onDisable = () => {
    if (!window.confirm('VDI für diesen Mandanten deaktivieren? Profile bleiben erhalten.')) return;
    void run(() => disableTenantVdi(slug, false), 'Deaktivierung');
  };

  if (!loaded) return <p className="text-slate-500 text-sm">Lade VDI…</p>;

  return (
    <section className="mb-6">
      <h3 className="font-semibold mb-2">VDI / Linux Terminal Server</h3>
      <p className="text-xs text-slate-500 mb-3">
        Apache Guacamole (Browser) + Linux-Desktops im Mandanten-Namespace.
        Browser-Zugang über <span className="font-mono">desktop-{slug}.{domain}</span>;
        nativer RDP-Client nur über das Headscale-Mesh. Zentrale Profile roamen
        zwischen den Desktops. Schneller Zugang ohne Kaltstart: minPool ≥ 1.
      </p>

      {err && <div className="bg-red-100 text-red-700 p-3 rounded mb-3 text-sm">{err}</div>}
      {msg && <div className="bg-green-100 text-green-700 p-3 rounded mb-3 text-sm">{msg}</div>}
      {cfg && !cfg.enabled && (
        <div className="bg-amber-50 text-amber-800 p-3 rounded mb-3 text-sm">
          VDI ist für diesen Mandanten nicht aktiviert.
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white rounded shadow p-4 space-y-3">
          <div>
            <label className="block text-sm text-slate-600 mb-1">Mindestanzahl VDIs (Warm-Pool)</label>
            <input type="number" min={0} max={100} step="1" value={minPool}
              onChange={(e) => setMinPool(e.target.value)} disabled={isReadOnly}
              className="w-full px-2 py-1 rounded border font-mono text-sm" />
            <p className="text-xs text-slate-400 mt-1">Vorgewärmte Desktops für geringe Ladezeit (0 = aus).</p>
          </div>
          <div>
            <label className="block text-sm text-slate-600 mb-1">Maximale Desktops</label>
            <input type="number" min={1} max={500} step="1" value={maxDesktops}
              onChange={(e) => setMaxDesktops(e.target.value)} disabled={isReadOnly}
              className="w-full px-2 py-1 rounded border font-mono text-sm" />
            <p className="text-xs text-slate-400 mt-1">Obergrenze (auch via ResourceQuota + Usage-Quota erzwungen).</p>
          </div>
          <div>
            <label className="block text-sm text-slate-600 mb-1">Idle-Timeout (Minuten)</label>
            <input type="number" min={5} max={1440} step="1" value={idleTimeoutMin}
              onChange={(e) => setIdleTimeoutMin(e.target.value)} disabled={isReadOnly}
              className="w-full px-2 py-1 rounded border font-mono text-sm" />
          </div>
          <div>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <input type="checkbox" checked={guacamole} onChange={(e) => setGuacamole(e.target.checked)}
                disabled={isReadOnly} />
              Guacamole (klassischer RDP-Zugang)
            </label>
            <p className="text-xs text-slate-400 mt-1">
              Aus: Desktops nur als Browser-Stream, ohne Guacamole-Server und ohne „Klassisch (RDP)“.
              Aufzeichnung, Abrechnung und Leerlauf laufen dann über die Streams.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 pt-1">
            {!cfg?.enabled && (
              <button onClick={onEnable} disabled={busy || isReadOnly || !valid} className="btn-primary disabled:opacity-50">
                VDI aktivieren
              </button>
            )}
            {cfg?.enabled && (
              <>
                <button onClick={onUpdate} disabled={busy || isReadOnly || !valid} className="btn-primary disabled:opacity-50">
                  Übernehmen
                </button>
                <button onClick={onDisable} disabled={busy || isReadOnly} className="btn-danger">
                  Deaktivieren
                </button>
              </>
            )}
          </div>
          {isReadOnly && <p className="text-xs text-slate-400">Read-only-Operator; VDI kann nicht bearbeitet werden.</p>}
        </div>

        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-slate-600">Status</h4>
          <div className="bg-white rounded shadow p-4 text-sm space-y-1">
            <div>Aktiviert: <span className="font-mono">{cfg?.enabled ? 'ja' : 'nein'}</span></div>
            <div>Warm-Pool: <span className="font-mono">{cfg?.minPool}</span></div>
            <div>Max. Desktops: <span className="font-mono">{cfg?.maxDesktops}</span></div>
            <div>Idle-Timeout: <span className="font-mono">{cfg?.idleTimeoutMin} min</span></div>
            <div>Guacamole: <span className="font-mono">{cfg ? (cfg.guacamole ? 'an' : 'aus') : '—'}</span></div>
            <div>Letzter Job: <span className="font-mono">{cfg?.lastJobId ?? '—'}</span></div>
          </div>
          <p className="text-xs text-slate-400">
            VDI-Nutzung &amp; Kosten erscheinen in der Sektion „Abrechnung &amp; Kosten“ (Metriken
            <span className="font-mono"> vdi.*</span>) und in OpenCost (Tenant-Label).
          </p>
        </div>
      </div>
      {cfg?.enabled && <VdiProfilesSection slug={slug} kind="linux" isReadOnly={isReadOnly} />}
      {cfg?.enabled && <VdiProfilesSection slug={slug} kind="windows" isReadOnly={isReadOnly} />}
    </section>
  );
}
